import { jsPDF } from 'jspdf';
import type { StructuredData } from '../api/types';
import type { ContractorSettings } from '../lib/contractorSettings';
import type { PdfLabels } from './pdfHelpers';
import {
  buildFooterRows,
  renderContractorDetails,
  renderItemsTable,
  renderLogo,
  renderNotesSection,
  renderPageFooter,
  resolveLabels,
} from './pdfHelpers';

export interface GenerateQuotePdfInput {
  structured_data: StructuredData;
  created_at: string;
  contractor: ContractorSettings;
  vatRate: number | null;
  markupPercent: number | null;
  quoteId?: string;
  paymentTermDays?: number;
  locale?: string;
  validityDays?: number;
  labels?: Partial<PdfLabels>;
  logo?: string | null;
  /** @deprecated Use labels.notesHeading instead */
  notesHeading?: string;
  /** @deprecated Use labels.validUntilLabel instead */
  validUntilLabel?: string;
}

interface MetadataConfig {
  structured_data: StructuredData;
  created_at: string;
  locale?: string;
  validityDays?: number;
  paymentTermDays?: number;
  quoteId?: string;
  labels: PdfLabels;
}

function renderMetadata(doc: jsPDF, config: MetadataConfig, startY: number): number {
  const { structured_data, created_at, locale, validityDays, paymentTermDays, quoteId, labels } =
    config;
  const pageWidth = doc.internal.pageSize.width;
  let yPos = startY;

  if (structured_data.client_name) {
    doc.text(`${labels.quoteFor}: ${structured_data.client_name}`, 14, yPos);
    yPos += 7;
  }

  if (structured_data.client_address) {
    doc.setFontSize(10);
    const addressLines = doc.splitTextToSize(structured_data.client_address, pageWidth - 28);
    doc.text(addressLines, 14, yPos);
    yPos += addressLines.length * 5;
    doc.setFontSize(11);
  }

  const dateLocale = locale || 'en-US';
  const formattedDate = new Date(created_at).toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.text(`${labels.dateLabel}: ${formattedDate}`, 14, yPos);
  yPos += 7;

  if (validityDays && validityDays > 0) {
    const validDate = new Date(created_at);
    validDate.setDate(validDate.getDate() + validityDays);
    const formattedValidDate = validDate.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    doc.text(`${labels.validUntilLabel}: ${formattedValidDate}`, 14, yPos);
    yPos += 7;
  }

  if (paymentTermDays && paymentTermDays > 0 && labels.paymentTerms) {
    doc.text(labels.paymentTerms, 14, yPos);
    yPos += 7;
  }

  if (quoteId) {
    doc.text(`${labels.refLabel}: ${quoteId.slice(0, 8).toUpperCase()}`, 14, yPos);
    yPos += 10;
  } else {
    yPos += 3;
  }

  return yPos;
}

const DEPRECATED_LABEL_KEYS = ['notesHeading', 'validUntilLabel'] as const;

function mergeDeprecatedLabels(input: GenerateQuotePdfInput): PdfLabels {
  const merged: Partial<PdfLabels> = { ...input.labels };
  for (const key of DEPRECATED_LABEL_KEYS) {
    if (input[key] && !input.labels?.[key]) {
      merged[key] = input[key];
    }
  }
  return resolveLabels(merged);
}

export function generatePdfFilename(clientName: string | null, createdAt: string): string {
  const name = clientName?.trim() || 'untitled';
  const date = createdAt.split('T')[0];
  return `quote-${name}-${date}.pdf`;
}

export function generateQuotePdf(input: GenerateQuotePdfInput): Blob {
  const {
    structured_data,
    created_at,
    contractor,
    vatRate,
    markupPercent,
    quoteId,
    paymentTermDays,
    locale,
    validityDays,
    logo,
  } = input;

  const labels = mergeDeprecatedLabels(input);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;

  // Logo (returns vertical offset, 0 when no logo)
  const logoOffset = renderLogo(doc, logo);

  // Header — company name or VoiceQuote
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(contractor.companyName || 'VoiceQuote', 14, 22 + logoOffset);

  // Contractor details (right-aligned block)
  renderContractorDetails(doc, contractor, pageWidth, true, labels, logoOffset);

  // Client name, address, date, validity, payment terms, ref
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const yPos = renderMetadata(
    doc,
    {
      structured_data,
      created_at,
      locale,
      validityDays,
      paymentTermDays,
      quoteId,
      labels,
    },
    35 + logoOffset,
  );

  // Build footer rows and render items table
  const footRows = buildFooterRows(structured_data.grand_total, vatRate, markupPercent, labels);
  renderItemsTable(doc, structured_data.items, footRows, yPos, vatRate, markupPercent, labels);

  // Notes section
  if (structured_data.notes) {
    renderNotesSection(doc, structured_data.notes, 10, labels.notesHeading);
  }

  // Footer
  renderPageFooter(doc, labels.generatedBy);

  return doc.output('blob');
}
